// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProjectAllocation {
  projectId: string;
  projectName: string;
  percent: number;
}

export interface MockProject {
  id: string;
  name: string;
  color: string;
}

export interface TeamMember {
  id: number;
  name: string;
  initials: string;
  avatarColor: string;
  role: string;
  department: string;
  reportsTo: number | null;
  lineManagerId: number | null;
  skills: Record<string, number>; // skill name → proficiency 1-5
  capacityPercent: number; // current utilisation 0-120
  burnoutFactors: {
    leaveBalanceDays: number;
    overtimeDays: number;
    consecutiveWorkMonths: number;
  };
  startDate: string; // ISO date
  isNewHire: boolean; // hired within last 30 days
  status: "active" | "on_leave" | "remote";
  allocations: ProjectAllocation[];
}

export interface CompanyEmployee {
  id: number;
  name: string;
  initials: string;
  avatarColor: string;
  role: string;
  department: string;
  lineManagerId: number | null;
  lineManagerName: string | null;
}

export type SkillColumn = string;

// ── Constants ─────────────────────────────────────────────────────────────────

export const SKILL_COLUMNS: SkillColumn[] = [
  "React",
  "TypeScript",
  "Node.js",
  "SQL",
  "Communication",
  "Leadership",
];

export const MOCK_PROJECTS: MockProject[] = [
  { id: "alpha", name: "Project Alpha", color: "#6366f1" },
  { id: "beta", name: "Project Beta", color: "#ec4899" },
  { id: "gamma", name: "Project Gamma", color: "#f59e0b" },
  { id: "delta", name: "Project Delta", color: "#10b981" },
  { id: "internal", name: "Internal / Admin", color: "#94a3b8" },
];

// ── Mock Data ─────────────────────────────────────────────────────────────────

export const MOCK_TEAM: TeamMember[] = [
  {
    id: 1,
    name: "Sarah Chen",
    initials: "SC",
    avatarColor: "#6366f1",
    role: "Engineering Manager",
    department: "Engineering Leadership",
    reportsTo: null,
    lineManagerId: null,
    skills: {
      React: 5,
      TypeScript: 5,
      "Node.js": 4,
      SQL: 3,
      Communication: 5,
      Leadership: 5,
    },
    capacityPercent: 85,
    burnoutFactors: {
      leaveBalanceDays: 12,
      overtimeDays: 3,
      consecutiveWorkMonths: 8,
    },
    startDate: "2021-03-15",
    isNewHire: false,
    status: "active",
    allocations: [
      { projectId: "alpha", projectName: "Project Alpha", percent: 40 },
      { projectId: "beta", projectName: "Project Beta", percent: 30 },
      { projectId: "internal", projectName: "Internal / Admin", percent: 15 },
    ],
  },
  {
    id: 2,
    name: "James Liu",
    initials: "JL",
    avatarColor: "#8b5cf6",
    role: "Senior Engineer",
    department: "Backend",
    reportsTo: 1,
    lineManagerId: 1,
    skills: {
      React: 5,
      TypeScript: 5,
      "Node.js": 5,
      SQL: 4,
      Communication: 4,
      Leadership: 3,
    },
    capacityPercent: 95,
    burnoutFactors: {
      leaveBalanceDays: 3,
      overtimeDays: 14,
      consecutiveWorkMonths: 11,
    },
    startDate: "2022-01-10",
    isNewHire: false,
    status: "active",
    allocations: [
      { projectId: "alpha", projectName: "Project Alpha", percent: 70 },
      { projectId: "beta", projectName: "Project Beta", percent: 25 },
    ],
  },
  {
    id: 3,
    name: "Priya Sharma",
    initials: "PS",
    avatarColor: "#ec4899",
    role: "Frontend Engineer",
    department: "Frontend",
    reportsTo: 1,
    lineManagerId: 1,
    skills: {
      React: 4,
      TypeScript: 4,
      "Node.js": 2,
      SQL: 2,
      Communication: 4,
      Leadership: 2,
    },
    capacityPercent: 70,
    burnoutFactors: {
      leaveBalanceDays: 18,
      overtimeDays: 1,
      consecutiveWorkMonths: 4,
    },
    startDate: "2023-06-01",
    isNewHire: false,
    status: "remote",
    allocations: [
      { projectId: "beta", projectName: "Project Beta", percent: 60 },
      { projectId: "gamma", projectName: "Project Gamma", percent: 40 },
    ],
  },
  {
    id: 4,
    name: "Marcus Webb",
    initials: "MW",
    avatarColor: "#f59e0b",
    role: "Backend Engineer",
    department: "Backend",
    reportsTo: 1,
    lineManagerId: 1,
    skills: {
      React: 2,
      TypeScript: 3,
      "Node.js": 5,
      SQL: 5,
      Communication: 3,
      Leadership: 2,
    },
    capacityPercent: 110,
    burnoutFactors: {
      leaveBalanceDays: 2,
      overtimeDays: 19,
      consecutiveWorkMonths: 14,
    },
    startDate: "2021-09-20",
    isNewHire: false,
    status: "active",
    allocations: [
      { projectId: "alpha", projectName: "Project Alpha", percent: 110 },
    ],
  },
  {
    id: 5,
    name: "Emma Torres",
    initials: "ET",
    avatarColor: "#10b981",
    role: "QA Engineer",
    department: "Quality Assurance",
    reportsTo: 1,
    lineManagerId: 1,
    skills: {
      React: 3,
      TypeScript: 3,
      "Node.js": 3,
      SQL: 3,
      Communication: 4,
      Leadership: 2,
    },
    capacityPercent: 55,
    burnoutFactors: {
      leaveBalanceDays: 22,
      overtimeDays: 0,
      consecutiveWorkMonths: 3,
    },
    startDate: "2023-03-08",
    isNewHire: false,
    status: "on_leave",
    allocations: [
      { projectId: "beta", projectName: "Project Beta", percent: 50 },
      { projectId: "internal", projectName: "Internal / Admin", percent: 50 },
    ],
  },
  {
    id: 6,
    name: "David Kim",
    initials: "DK",
    avatarColor: "#3b82f6",
    role: "DevOps Engineer",
    department: "Platform & DevOps",
    reportsTo: 1,
    lineManagerId: 1,
    skills: {
      React: 2,
      TypeScript: 2,
      "Node.js": 4,
      SQL: 4,
      Communication: 3,
      Leadership: 3,
    },
    capacityPercent: 80,
    burnoutFactors: {
      leaveBalanceDays: 8,
      overtimeDays: 5,
      consecutiveWorkMonths: 6,
    },
    startDate: "2022-07-01",
    isNewHire: false,
    status: "active",
    allocations: [
      { projectId: "alpha", projectName: "Project Alpha", percent: 80 },
      { projectId: "internal", projectName: "Internal / Admin", percent: 20 },
    ],
  },
  {
    id: 7,
    name: "Aisha Patel",
    initials: "AP",
    avatarColor: "#f97316",
    role: "Junior Engineer",
    department: "Frontend",
    reportsTo: 2,
    lineManagerId: 1,
    skills: {
      React: 2,
      TypeScript: 2,
      "Node.js": 2,
      SQL: 2,
      Communication: 3,
      Leadership: 1,
    },
    capacityPercent: 65,
    burnoutFactors: {
      leaveBalanceDays: 20,
      overtimeDays: 0,
      consecutiveWorkMonths: 2,
    },
    startDate: "2026-02-10",
    isNewHire: true,
    status: "active",
    allocations: [
      { projectId: "gamma", projectName: "Project Gamma", percent: 50 },
      { projectId: "internal", projectName: "Internal / Admin", percent: 50 },
    ],
  },
  {
    id: 8,
    name: "Noah Fischer",
    initials: "NF",
    avatarColor: "#14b8a6",
    role: "Junior Engineer",
    department: "Backend",
    reportsTo: 2,
    lineManagerId: 1,
    skills: {
      React: 3,
      TypeScript: 2,
      "Node.js": 2,
      SQL: 1,
      Communication: 3,
      Leadership: 1,
    },
    capacityPercent: 70,
    burnoutFactors: {
      leaveBalanceDays: 22,
      overtimeDays: 0,
      consecutiveWorkMonths: 1,
    },
    startDate: "2026-03-01",
    isNewHire: true,
    status: "active",
    allocations: [
      { projectId: "beta", projectName: "Project Beta", percent: 70 },
      { projectId: "internal", projectName: "Internal / Admin", percent: 30 },
    ],
  },
];

// ── Company-wide employee pool (for Add Employee dialog) ──────────────────────

export const MOCK_COMPANY_EMPLOYEES: CompanyEmployee[] = [
  {
    id: 101,
    name: "Alex Johnson",
    initials: "AJ",
    avatarColor: "#06b6d4",
    role: "Product Manager",
    department: "Product",
    lineManagerId: null,
    lineManagerName: null,
  },
  {
    id: 102,
    name: "Maya Okonkwo",
    initials: "MO",
    avatarColor: "#a855f7",
    role: "UX Designer",
    department: "Design",
    lineManagerId: null,
    lineManagerName: null,
  },
  {
    id: 103,
    name: "Carlos Rivera",
    initials: "CR",
    avatarColor: "#ef4444",
    role: "Data Analyst",
    department: "Analytics",
    lineManagerId: 201,
    lineManagerName: "Robert Park",
  },
  {
    id: 104,
    name: "Linda Zhang",
    initials: "LZ",
    avatarColor: "#84cc16",
    role: "Marketing Specialist",
    department: "Marketing",
    lineManagerId: null,
    lineManagerName: null,
  },
  {
    id: 105,
    name: "Tom Eriksson",
    initials: "TE",
    avatarColor: "#f97316",
    role: "Backend Engineer",
    department: "Engineering",
    lineManagerId: 201,
    lineManagerName: "Robert Park",
  },
  {
    id: 106,
    name: "Fatima Al-Hassan",
    initials: "FA",
    avatarColor: "#0ea5e9",
    role: "QA Engineer",
    department: "Quality Assurance",
    lineManagerId: null,
    lineManagerName: null,
  },
];

// Helper: convert a CompanyEmployee to a minimal TeamMember when added to team
export function companyEmployeeToTeamMember(
  e: CompanyEmployee,
  managerId: number | null,
): TeamMember {
  return {
    id: e.id,
    name: e.name,
    initials: e.initials,
    avatarColor: e.avatarColor,
    role: e.role,
    department: e.department,
    reportsTo: managerId,
    lineManagerId: managerId,
    skills: {
      React: 0,
      TypeScript: 0,
      "Node.js": 0,
      SQL: 0,
      Communication: 0,
      Leadership: 0,
    },
    capacityPercent: 0,
    burnoutFactors: { leaveBalanceDays: 0, overtimeDays: 0, consecutiveWorkMonths: 0 },
    startDate: new Date().toISOString().split("T")[0],
    isNewHire: true,
    status: "active",
    allocations: [],
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function computeBurnoutScore(m: TeamMember): number {
  let score = 0;
  // Low leave balance → risk
  if (m.burnoutFactors.leaveBalanceDays < 5) score += 40;
  else if (m.burnoutFactors.leaveBalanceDays < 10) score += 15;
  // High overtime → risk
  if (m.burnoutFactors.overtimeDays > 10) score += 35;
  else if (m.burnoutFactors.overtimeDays > 5) score += 15;
  // Long consecutive work stretch → risk
  if (m.burnoutFactors.consecutiveWorkMonths > 12) score += 25;
  else if (m.burnoutFactors.consecutiveWorkMonths > 8) score += 10;
  return Math.min(score, 100);
}

export type BurnoutLevel = "low" | "medium" | "high";

export function burnoutLevel(score: number): BurnoutLevel {
  if (score >= 60) return "high";
  if (score >= 30) return "medium";
  return "low";
}

export function capacityLevel(pct: number): "normal" | "busy" | "over" {
  if (pct > 100) return "over";
  if (pct > 80) return "busy";
  return "normal";
}
